import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import {
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import dayjs from "dayjs";
import type { NextPage } from "next";
import Image from "next/image";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { css } from "styled-components";

import { getAuth } from "firebase/auth";
import { FantasyBadge } from "../../components/Badge";
import { Card } from "../../components/Card";
import { Flex } from "../../components/Grid";
import { LockedOverlay } from "../../components/LockedOverlay";
import { Table } from "../../components/Table";
import { Toggle } from "../../components/Toggle";
import {
  Callout,
  Caption1,
  Footnote,
  Headline,
  LargeTitle,
  Subhead,
  Title2,
} from "../../components/Typography";
import { Button } from "../../components/buttons/Button";
import { Input } from "../../components/inputs/Input";
import { Tabs } from "../../components/navigation/Tabs";
import { BackButton } from "../../components/navigation/subnav/BackButton";
import {
  DropEntry,
  MarketplaceActivity,
  MarketplaceListing,
  Mint,
} from "../../dropTypes";
import {
  useFirestoreCacheKey,
  useFirestoreInfiniteQuery,
  useFirestoreQuery,
} from "../../hooks/firestore";
import { useDeeplinkPath, useDynamicLink } from "../../hooks/links";
import { useSnackbar } from "../../hooks/snackbar";
import { truncatedWalletStr, useSocialWalletAddress } from "../../hooks/wallet";
import { SecondaryPurchaseModal } from "../../layouts/drop/buy/purchase";
import { QRCodeImage } from "../../layouts/drop/qrcode";
import { SoldIndicator } from "../../layouts/drop/soldIndicator";
import { UserDetails } from "../../layouts/drop/userDetails";
import { PageLayout, PageTypes } from "../../layouts/page";
import { Placeholder } from "../../layouts/profile/placeholder";
import { LoadingPlaceholder } from "../../layouts/progress";
import {
  cancelWithSocialWallet,
  fetchCooldownStatus,
  fetchMetadataByMintId,
  sellWithSocialWallet,
  updateWithSocialWallet,
} from "../../lib/backend";
import type { CompoundWhereClause, FetchWhereArgs } from "../../lib/firestore";
import {
  mediumScreenAndAbove,
  mediumScreenAndBelow,
  smallScreenAndAbove,
  smallScreenAndBelow,
  xLargeScreenAndAbove,
} from "../../lib/mediaQuery";
import { formatDollarsFromCents } from "../../utils";
import {
  dropTypePrettifier,
  marketActivityPrettifier,
} from "../../utils/strings";

const VaultDetailsPage: NextPage = () => {
  const router = useRouter();
  const mintQuery = useQuery(
    ["mint", router.query.address],
    () => fetchMetadataByMintId(router.query.address as string),
    {
      enabled: Boolean(router.query.address),
    }
  );
  const mint = mintQuery.data;
  const whereClause: CompoundWhereClause = useMemo(
    () => [
      ["collectionMints", "array-contains-any", [mint?.nft.collection.address]],
    ],
    [mint]
  );
  const dropQuery = useFirestoreQuery<DropEntry>(
    {
      collection: "drops",
      where: whereClause,
    },
    Boolean(mint)
  );

  const socialWalletAddress = useSocialWalletAddress();

  let backButtonDestination = "../";
  let backButtonText = "BACK HOME";

  const owner = mintQuery.data?.metadata?.owner;
  if (owner && !PublicKey.isOnCurve(owner.wallet)) {
    backButtonDestination = `../profile/${owner.wallet}`;
    backButtonText = "BACK TO COLLECTION";
  } else if (owner && !PublicKey.isOnCurve(owner.delegate)) {
    backButtonDestination = `../profile/${owner.delegate}`;
    backButtonText = "BACK TO COLLECTION";
  }

  const displayQR =
    owner?.delegate === socialWalletAddress ||
    owner?.wallet === socialWalletAddress;

  const drop = dropQuery.data?.[0];

  const largeVaultImage = (
    <Image
      alt="Vault Image"
      src={mintQuery.data?.metadata.image}
      height={300}
      width={300}
      style={{
        objectFit: "cover",
        objectPosition: "center",
        borderRadius: "4px",
      }}
    />
  );

  if (!mintQuery.data) {
    return <LoadingPlaceholder />;
  }

  return (
    <PageLayout
      pageType={PageTypes.NORMAL}
      backgroundType="gradient"
      subNavContent={
        <BackButton
          label={backButtonText}
          destination={backButtonDestination}
          css={`
            ${mediumScreenAndBelow(css`
              display: block;
            `)}
          `}
        />
      }
    >
      {mintQuery.data ? (
        <>
          <Flex
            gap="lg"
            direction="column"
            xlDirection="row"
            paddingLeft="xs"
            paddingRight="xs"
            css={`
              ${smallScreenAndBelow(css`
                gap: var(--spacing-md);
              `)}
            `}
          >
            <Flex
              css={`
                display: none;
                ${xLargeScreenAndAbove(css`
                  display: flex;
                `)}
              `}
            >
              {largeVaultImage}
            </Flex>
            <DropDetails
              drop={drop}
              mint={mintQuery.data}
              displayQR={displayQR}
            />
            <Flex gap="lg">
              <Flex
                css={`
                  display: none;
                  ${mediumScreenAndAbove(css`
                    display: flex;
                  `)}
                  ${xLargeScreenAndAbove(css`
                    display: none;
                  `)}
                `}
              >
                {largeVaultImage}
              </Flex>
              <Flex
                flex={1}
                direction="column"
                css={`
                  min-width: 300px;
                  ${smallScreenAndBelow(css`
                    flex-direction: column-reverse;
                  `)}
                `}
              >
                <Actions mint={mintQuery.data} drop={drop} />
                <div>
                  <Headline>Actions</Headline>
                  <OpenInApp drop={dropQuery.data?.[0]} />
                  <Button
                    href={`/drop/${drop?.id}`}
                    shape="rectangle"
                    variant="semi-transparent"
                    border
                    disabled={!drop}
                    css={`
                      margin-top: var(--spacing-xs);
                    `}
                  >
                    View Drop Details
                  </Button>
                  <Button
                    href={`/${drop?.creator?.slug}`}
                    shape="rectangle"
                    disabled={!drop}
                    variant="semi-transparent"
                    border
                    css={`
                      margin-top: var(--spacing-xs);
                    `}
                  >
                    View Artist Profile
                  </Button>
                </div>
              </Flex>
            </Flex>
          </Flex>
          <Activity mint={mintQuery.data} />
        </>
      ) : (
        <LoadingPlaceholder />
      )}
    </PageLayout>
  );
};

interface OpenInAppProps {
  drop?: DropEntry;
}

const OpenInApp = ({ drop }: OpenInAppProps) => {
  const path = useDeeplinkPath({
    pathType: "myDmcs",
    drop,
  });
  const dynamicLink = useDynamicLink("myDmcsDynamicLink", path);

  return (
    <Button
      variant={"primary"}
      shape={"rectangle"}
      css={`
        margin-top: var(--spacing-xs);
        ${smallScreenAndAbove(css`
          display: none;
        `)}
      `}
      disabled={!drop}
      onClick={() => window?.open(dynamicLink, "_blank")}
    >
      Open In-App
    </Button>
  );
};

interface ItemDetailProps {
  label: string;
  value: string;
}

const ItemDetail = ({ label, value }: ItemDetailProps) => (
  <Flex
    flex={1}
    direction="column"
    css={`
      padding: var(--spacing-xs) 0;
      border-bottom: 1px solid rgb(var(--yellow-mid));
    `}
  >
    <Footnote>{label}</Footnote>
    <Headline>{value}</Headline>
  </Flex>
);

interface DropDetailsProps {
  drop?: DropEntry;
  mint?: Mint;
  displayQR: boolean;
}

const DropDetails = ({ drop, mint, displayQR }: DropDetailsProps) => {
  const path = useDeeplinkPath({
    pathType: "mintedDmc",
    mintId: mint?.nft.address,
  });
  const dynamicLink = useDynamicLink("IAPMintedDrop", path);
  const creator = mint?.creators[0];
  const dropType = dropTypePrettifier(
    drop?.variants?.[0]?.collectionMetadata?.type
  );
  const isFantasy = drop?.filters.includes("fantasy_manager_true");

  const collectionListingsQuery = useFirestoreQuery<MarketplaceListing>({
    collection: "marketplace_listings",
    where: [["collection", "==", mint?.nft?.collection?.address]],
  });

  const latestSaleQuery: UseQueryResult<MarketplaceActivity[], unknown> =
    useFirestoreQuery({
      collection: "marketplace_activity",
      where: [
        ["mint", "==", mint.nft.address],
        ["type", "in", [ActivityType.PrimarySale, ActivityType.SecondarySale]],
      ],
      orderBy: ["createdAt", "desc"],
      limit: 1,
    });

  const primaryMktAvailability = drop?.variants[0]?.itemsRemaining;
  const secondaryMktAvailability = collectionListingsQuery?.data?.length ?? 0;
  const mktAvailability = primaryMktAvailability + secondaryMktAvailability;

  const smallVaultImage = mint ? (
    <Image
      alt="Vault Image"
      src={mint.metadata.image}
      height={85}
      width={85}
      style={{
        objectFit: "cover",
        objectPosition: "center",
        borderRadius: "8px",
      }}
    />
  ) : null;

  const vaultName = mint?.metadata.name
    ?.replace("#" + mint?.metadata.collectionNumber.toString(), "")
    .trim();

  return (
    <Flex flex={1} direction="column">
      <Flex
        direction="row"
        gap="md"
        css={`
          margin-bottom: var(--spacing-md);
        `}
      >
        <Flex collapse="md" flex={0}>
          {smallVaultImage}
        </Flex>
        <Flex flex={1} direction="column">
          <UserDetails
            verified
            name={creator?.name}
            slug={creator?.slug}
            image={creator?.image}
            css={`
              flex: 0;
              margin-bottom: var(--spacing-xxs);
            `}
          />
          {/* ===== Large Title ==== */}
          <Flex
            css={`
              display: none;
              ${mediumScreenAndAbove(css`
                display: flex;
              `)}
            `}
          >
            <LargeTitle>{vaultName}</LargeTitle>
          </Flex>
          {/* ===== Small Title ==== */}
          <Flex collapse="md">
            <Headline>{vaultName}</Headline>
          </Flex>
          <Flex direction="column" lgDirection="row" lgAlign="center" gap="xs">
            <Callout>
              <span
                css={`
                  text-transform: uppercase;
                `}
              >
                DIGITAL {dropType}
              </span>
              &nbsp;·&nbsp;
              <span
                css={`
                  ${smallScreenAndBelow(css`
                    display: none;
                  `)}
                `}
              >
                {drop?.variants[0]?.itemsLoaded ?? "..."} EDITIONS
              </span>
              <span
                css={`
                  display: none;
                  ${smallScreenAndBelow(css`
                    display: inline;
                  `)}
                `}
              >
                {mint?.metadata.collectionNumber}/
                {drop?.variants[0]?.itemsLoaded ?? "..."}
              </span>
            </Callout>
            {isFantasy && <FantasyBadge />}
          </Flex>
        </Flex>
        <Flex gap="md">
          <Flex direction="column" justify="center" mdJustify="flex-start">
            <LargeTitle
              css={`
                text-align: right;
                ${smallScreenAndBelow(css`
                  font-size: 20px;
                  line-height: 1.5;
                `)}
              `}
            >
              #{mint?.metadata.collectionNumber}
            </LargeTitle>

            <Card
              css={`
                width: auto;
                padding: var(--spacing-xxs) var(--spacing-xs);
                border-radius: var(--border-radius-xs);
              `}
            >
              <Subhead>Common</Subhead>
            </Card>
          </Flex>
          <div
            css={`
              display: none;
              ${mediumScreenAndAbove(css`
                display: block;
              `)}
            `}
          >
            {displayQR ? (
              <QRCodeImage
                link={dynamicLink}
                height={74}
                width={74}
                margin={0}
              />
            ) : (
              <></>
            )}
          </div>
        </Flex>
      </Flex>
      <Flex gap="lg">
        <ItemDetail
          label={"Item Value"}
          value={
            drop
              ? `${formatDollarsFromCents(
                  Number(drop?.variants[0]?.priceUsdCents)
                )}`
              : "..."
          }
        />
        <ItemDetail
          label={"Last Sale"}
          value={`${
            latestSaleQuery?.data?.[0]?.amount
              ? formatDollarsFromCents(
                  latestSaleQuery?.data?.[0]?.amount / 10_000
                )
              : "..."
          }`}
        />
        <ItemDetail
          label={"On Sale"}
          value={
            drop
              ? `${mktAvailability}/${drop?.variants[0]?.itemsLoaded}`
              : "..."
          }
        />
      </Flex>
      <Flex direction="column" lgDirection="row" gap="md" paddingTop="md">
        <Flex flex={1}>
          {drop && (
            <SoldIndicator
              totalKeys={drop?.variants[0]?.itemsLoaded}
              soldKeys={
                drop?.variants[0]?.itemsLoaded -
                drop?.variants[0]?.itemsRemaining
              }
              isEnded={drop?.state?.kind === "Ended"}
              isFree={Number(drop?.variants[0]?.priceUsdCents) === 0}
            />
          )}
        </Flex>
        {/* <Flex flex={1} /> */}
      </Flex>
    </Flex>
  );
};

interface ActionsProps {
  mint: Mint;
  drop: DropEntry;
}

interface SellForm {
  amount: string;
}

const Actions = ({ mint, drop }: ActionsProps) => {
  const queryClient = useQueryClient();
  const { connection } = useConnection();
  const methods = useForm<SellForm>();
  const pushSnack = useSnackbar();

  const wallet = useWallet();
  const socialWalletAddress = useSocialWalletAddress();
  const whereArgs: FetchWhereArgs = useMemo(
    () => ({
      collection: "marketplace_listings",
      where: [["mint", "==", mint.nft.address]],
    }),
    [mint]
  );
  const cacheKey = useFirestoreCacheKey(whereArgs);
  const query = useFirestoreQuery<MarketplaceListing>(whereArgs);
  const listing: MarketplaceListing = query.data?.[0];

  const owner = mint?.metadata?.owner;
  const isSeller =
    owner?.wallet === socialWalletAddress ||
    owner?.delegate === socialWalletAddress;
  const isExternalSeller =
    owner?.wallet && owner?.wallet === wallet.publicKey?.toBase58();

  const cooldownStatusQuery = useQuery(
    ["escrow_data", mint.nft.address],
    async () => {
      const statuses = await fetchCooldownStatus(connection, [
        mint.nft.address,
      ]);
      if (mint.nft.address in statuses) {
        return statuses[mint.nft.address];
      } else {
        return true;
      }
    }
  );
  const hasCooldown = cooldownStatusQuery.data === false;

  useEffect(() => {
    if (listing) {
      methods.setValue("amount", Number(listing.amount / 1_000_000).toFixed(2));
    }
  }, [listing]);

  const amount = methods.watch("amount");
  const amountAfterFees = useMemo(() => {
    if (!amount) return 0;
    return Number(
      Number(amount) - Number(amount) * (mint.nft.sellerFeeBasisPoints / 10_000)
    ).toFixed(2);
  }, [mint, amount]);

  const listMutation = useMutation(
    async (amount: string) => {
      const tokenAccount = await connection.getTokenLargestAccounts(
        new PublicKey(mint.nft.address)
      );

      if (!tokenAccount.value) {
        throw new Error("No token account found");
      }

      return sellWithSocialWallet(
        mint.nft.address,
        tokenAccount.value[0].address.toBase58(),
        // Convert dollar amount to 6 decimal
        Number(amount) * 1_000_000
      );
    },
    {
      onError(err) {
        console.log("error: ", err);
        pushSnack({
          key: "listing-error",
          message: "Something went wrong",
          type: "error",
        });
      },
      onSuccess(_, amount: string) {
        const newListing: MarketplaceListing = {
          // Placeholder - only needed for buying
          id: "",
          // Convert dollar amount to 6 decimal
          amount: Number(amount) * 1_000_000,
          collection: mint.nft.collection.address,
          edition: mint.metadata.collectionNumber,
          image: mint.metadata.image,
          mint: mint.nft.address,
          name: mint.nft.name,
          seller: socialWalletAddress,
          // placeholder - only needed for buying
          sellerHashedGuid: "",
          createdAt: Date.now(),
          updatedAt: null,
          creator: mint.creators[0],
          type: mint.metadata.type,
        };

        queryClient.setQueryData(cacheKey, [newListing]);
        methods.reset();

        pushSnack({
          key: "listing-success",
          message: "Vault listed",
          type: "success",
        });
      },
    }
  );

  const delistMutation = useMutation(
    async () => {
      const tokenAccount = await connection.getTokenLargestAccounts(
        new PublicKey(mint.nft.address)
      );

      if (!tokenAccount.value) {
        throw new Error("No token account found");
      }

      return cancelWithSocialWallet(
        mint.nft.address,
        tokenAccount.value[0].address.toBase58()
      );
    },
    {
      onError(err) {
        console.log("error: ", err);
        pushSnack({
          key: "delisting-error",
          message: "Something went wrong",
          type: "error",
        });
      },
      onSuccess() {
        queryClient.setQueryData(cacheKey, []);
        pushSnack({
          key: "delisting-success",
          message: "Vault delisted",
          type: "success",
        });
      },
    }
  );

  const updateMutation = useMutation(
    async (amount: string) => {
      const tokenAccount = await connection.getTokenLargestAccounts(
        new PublicKey(mint.nft.address)
      );

      if (!tokenAccount.value) {
        throw new Error("No token account found");
      }

      return updateWithSocialWallet(
        mint.nft.address,
        // Convert dollar amount to 6 decimal
        Number(amount) * 1_000_000
      );
    },
    {
      onError(err) {
        console.log("error: ", err);
        pushSnack({
          key: "update-listing",
          message: "Update listing failed, please try again later!",
          type: "error",
        });
      },
      onSuccess() {
        queryClient.setQueryData(cacheKey, (listings: MarketplaceListing[]) => [
          {
            ...listings[0],
            amount: Number(amount) * 1_000_000,
            updatedAt: Date.now(),
          },
        ]);
        methods.reset();

        pushSnack({
          key: "update-listing",
          message: "Listing updated",
          type: "success",
        });
      },
    }
  );

  function renderButtons() {
    if (isSeller || isExternalSeller) {
      if (listing) {
        return (
          <Flex gap="xs" width="186px">
            <Button
              type="submit"
              disabled={!mint}
              variant="primary-alt"
              shape="rectangle"
              fontSize={13}
              css={`
                margin: 0;
              `}
              loading={updateMutation.isLoading}
            >
              Update
            </Button>
            <Button
              disabled={!mint}
              loading={delistMutation.isLoading}
              variant="primary-alt"
              shape="rectangle"
              fontSize={13}
              css={`
                margin: 0;
              `}
              onClick={() => delistMutation.mutate()}
            >
              Delist
            </Button>
          </Flex>
        );
      }
      return (
        <Flex width="102px">
          <Button
            type="submit"
            variant="primary-alt"
            shape="rectangle"
            fontSize={13}
            css={`
              margin: 0;
            `}
            disabled={hasCooldown}
            loading={listMutation.isLoading}
          >
            List Now
          </Button>
        </Flex>
      );
    }

    if (listing) {
      return (
        <Flex width="102px">
          <BuyNow dropId={drop?.id} listingId={listing.id} mint={mint} />
        </Flex>
      );
    }

    return null;
  }

  if (!listing && !isSeller && !isExternalSeller) {
    return null;
  }

  return (
    <form
      noValidate
      onSubmit={methods.handleSubmit((data) =>
        !listing
          ? listMutation.mutate(data.amount)
          : updateMutation.mutate(data.amount)
      )}
      css={`
        position: relative;
      `}
    >
      <Headline
        css={`
          margin-bottom: var(--spacing-xs);
          ${smallScreenAndBelow(css`
            margin-top: var(--spacing-md);
          `)}
        `}
      >
        {listing ? (isSeller ? "Sell Item" : "Buy Item") : "Sell Item"}
      </Headline>
      <Flex gap="xs" height="42px" align="center">
        <Flex flex={1} dir="column">
          {listing && !isSeller ? (
            <div
              css={`
                padding: 0 var(--spacing-sm);
                background: var(--input-background-color);
                width: 100%;
                border-radius: var(--border-radius-xs);
              `}
            >
              <Callout
                css={`
                  font-weight: 600;
                  line-height: 42px;
                `}
              >
                ${Number(listing.amount / 1_000_000).toFixed(2)}
              </Callout>
            </div>
          ) : (
            <Controller
              name="amount"
              control={methods.control}
              rules={{
                required: true,
                validate(value) {
                  const parsed = Number(value);
                  if (isNaN(parsed)) return false;
                  if (parsed <= 0) return false;
                  return true;
                },
              }}
              render={({ field }) => {
                return (
                  <Input
                    {...field}
                    name="amount"
                    placeholder="List Price (USD)"
                    error={methods.formState.errors.amount}
                    disabled={!isSeller || hasCooldown}
                    onBlur={() => {
                      const value = methods.getValues("amount");

                      if (isNaN(Number(value))) return value;

                      const formatted = Number(value).toFixed(2);
                      field.onChange(formatted);
                      field.onBlur();
                    }}
                  />
                );
              }}
            />
          )}
        </Flex>
        {renderButtons()}
      </Flex>
      <Flex
        css={`
          padding-top: var(--spacing-xs);
          margin-bottom: var(--spacing-md);
        `}
      >
        <Caption1>
          By clicking {isSeller ? "“List Now”" : "“Buy Now”"} you agree to the{" "}
          <a href="/terms-of-service" target="_blank">
            terms of use
          </a>{" "}
          and{" "}
          <a href="/privacy-policy" target="_blank">
            privacy policy
          </a>
          .
        </Caption1>
      </Flex>
      <Card
        css={`
          padding: var(--spacing-xs) var(--spacing-sm);
          border-radius: 4px;
          background: rgba(255, 255, 255, 0.1);
        `}
      >
        <Flex
          justify="space-between"
          css={`
            margin-bottom: var(--spacing-xxs);
          `}
        >
          <Callout>Artist Royalty</Callout>
          <Callout>{mint.nft.sellerFeeBasisPoints / 100}%</Callout>
        </Flex>
        <Flex justify="space-between">
          <Callout>You'll Receive</Callout>
          <Callout
            css={`
              font-weight: 600;
            `}
          >
            ${amountAfterFees}
          </Callout>
        </Flex>
      </Card>
      {hasCooldown ? (
        <LockedOverlay
          title="Listing Locked"
          body="Listing can only be initiated 60 days after credit card purchases."
        />
      ) : isExternalSeller ? (
        <LockedOverlay
          title="External Wallet"
          body="Vaults must be transferred to your Vault Wallet to be listed for sale."
        />
      ) : null}
    </form>
  );
};

interface BuyNowProps {
  dropId: string;
  listingId: string;
  mint: Mint;
}

const BuyNow = ({ dropId, listingId, mint }: BuyNowProps) => {
  const socialWalletAddress = useSocialWalletAddress();
  const [open, setModal] = useState(false);

  return (
    <>
      <Button
        variant="primary"
        shape="rectangle"
        fontSize={13}
        css={`
          margin: 0;
        `}
        disabled={!dropId || !socialWalletAddress}
        onClick={() => setModal(true)}
      >
        Buy Now
      </Button>
      <SecondaryPurchaseModal
        mintAddress={mint.nft.address}
        dropId={dropId}
        listingId={listingId}
        open={open}
        onRequestClose={() => setModal(false)}
      />
    </>
  );
};

interface AcivityProps {
  mint?: Mint;
}

enum TableType {
  Item = "Item",
  Collection = "Collection",
}

const COLLECTION_ACTIVITY_TABS = [
  { name: "All Activity" },
  { name: "Latest Sales" },
  { name: "Top Sales" },
  { name: "Listings" },
];

const ITEM_ACTIVITY_TABS = [{ name: "All Activity" }, { name: "Sales" }];

const MAX_TABLE_ROWS = 10;

enum ActivityType {
  Listing = "LISTING",
  UpdateListing = "UPDATE_LISTING",
  Delisting = "DELISTING",
  PrimarySale = "PRIMARY_SALE",
  SecondarySale = "SECONDARY_SALE",
}

const Activity = ({ mint }: AcivityProps) => {
  const [activeTable, setActiveTable] = useState(TableType.Item);
  const [activeItemTab, setActiveItemTab] = useState(ITEM_ACTIVITY_TABS[0]);
  const [activeCollectionTab, setActiveCollectionTab] = useState(
    ITEM_ACTIVITY_TABS[0]
  );
  const auth = getAuth();
  const firestoreUserQuery = useFirestoreQuery<any>(
    {
      collection: "users",
      where: [["uid", "==", auth?.currentUser?.uid]],
    },
    Boolean(auth?.currentUser?.uid)
  );
  const socialWalletAddress =
    firestoreUserQuery?.data?.[0]?.socialWalletAddress;

  const queryArgs: FetchWhereArgs = useMemo(() => {
    if (!mint) return;

    switch (activeTable) {
      case TableType.Collection: {
        switch (activeCollectionTab.name) {
          case "All Activity": {
            return {
              collection: "marketplace_activity",
              where: [["collection", "==", mint.nft.collection.address]],
              orderBy: ["createdAt", "desc"],
              limit: MAX_TABLE_ROWS,
            };
          }
          case "Latest Sales": {
            return {
              collection: "marketplace_activity",
              where: [
                ["collection", "==", mint.nft.collection.address],
                [
                  "type",
                  "in",
                  [ActivityType.SecondarySale, ActivityType.PrimarySale],
                ],
              ],
              orderBy: ["createdAt", "desc"],
              limit: MAX_TABLE_ROWS,
            };
          }
          case "Top Sales": {
            return {
              collection: "marketplace_activity",
              where: [
                ["collection", "==", mint.nft.collection.address],
                [
                  "type",
                  "in",
                  [ActivityType.SecondarySale, ActivityType.PrimarySale],
                ],
              ],
              orderBy: ["amount", "desc"],
              limit: MAX_TABLE_ROWS,
            };
          }
          case "Listings": {
            return {
              collection: "marketplace_activity",
              where: [
                ["collection", "==", mint.nft.collection.address],
                ["type", "==", ActivityType.Listing],
              ],
              orderBy: ["createdAt", "desc"],
              limit: MAX_TABLE_ROWS,
            };
          }
        }

        break;
      }

      case TableType.Item: {
        switch (activeItemTab.name) {
          case "All Activity": {
            return {
              collection: "marketplace_activity",
              where: [["mint", "==", mint.nft.address]],
              orderBy: ["createdAt", "desc"],
              limit: MAX_TABLE_ROWS,
            };
          }
          case "Sales": {
            return {
              collection: "marketplace_activity",
              where: [
                ["mint", "==", mint.nft.address],
                [
                  "type",
                  "in",
                  [ActivityType.SecondarySale, ActivityType.PrimarySale],
                ],
              ],
              orderBy: ["createdAt", "desc"],
              limit: MAX_TABLE_ROWS,
            };
          }
        }
      }
    }
  }, [mint, activeTable, activeItemTab, activeCollectionTab]);

  const query = useFirestoreInfiniteQuery<MarketplaceActivity>(queryArgs);

  const allData: MarketplaceActivity[] = query.data?.pages?.reduce(
    (acc, page) => [...acc, ...page.data],
    []
  );

  function renderTable() {
    if (!mint || query.isLoading) {
      return <LoadingPlaceholder />;
    }

    return allData?.length ? (
      <Table
        dataToHeadersMap={{
          name: "Vault",
          type: "Type",
          value: "Value",
          seller: "Seller",
          buyer: "Buyer",
          date: "Date / Time",
        }}
        data={allData.map((activity) => {
          const activityType = marketActivityPrettifier(
            activity.type,
            socialWalletAddress === activity.seller
          );
          return {
            id: activity.id,
            name: activity.name,
            type: activityType,
            value: formatDollarsFromCents(activity.amount / 10_000),
            seller: activity.seller
              ? truncatedWalletStr(activity.seller, 5)
              : "-",
            buyer: activity.buyer ? truncatedWalletStr(activity.buyer, 5) : "-",
            date: dayjs(activity.createdAt).format("MMM DD YYYY | HH:mm:ss"),
          };
        })}
      />
    ) : (
      <Flex direction={"column"}>
        <Placeholder variant="collectionActivitySecondaryMkt" />
      </Flex>
    );
  }

  return (
    <>
      <Flex
        justify="space-between"
        direction="column"
        lgDirection="row"
        css={`
          margin-top: var(--spacing-md);
          padding-left: var(--spacing-xs);
          padding-right: var(--spacing-xs);
        `}
      >
        <Title2
          css={`
            margin-bottom: var(--spacing-md);
          `}
        >
          Activity
        </Title2>
        <div>
          <Toggle
            variant="glass"
            size="medium"
            options={[TableType.Item, TableType.Collection]}
            toggleCallback={() =>
              setActiveTable((active) =>
                active === TableType.Collection
                  ? TableType.Item
                  : TableType.Collection
              )
            }
          />
        </div>
      </Flex>
      <div
        css={`
          margin-top: var(--spacing-md);
          padding-left: var(--spacing-xs);
          padding-right: var(--spacing-xs);
        `}
      >
        {activeTable === TableType.Collection ? (
          <Tabs
            tabs={COLLECTION_ACTIVITY_TABS}
            callback={setActiveCollectionTab}
          />
        ) : (
          <Tabs tabs={ITEM_ACTIVITY_TABS} callback={setActiveItemTab} />
        )}
        {renderTable()}
      </div>
    </>
  );
};

export default VaultDetailsPage;
